import {
   useGetFubStages,
   useGetPeopleTags,
   useGetSuggestedStageMappings,
} from '@forwardflow/queries/onboarding.queries'
import { IStage, useAppStore } from '@forwardflow/store'
import { Button, SegmentControl } from '@forwardflow/ui-v2'
import { useState, useEffect, useMemo } from 'react'
import {
   DndContext,
   DragEndEvent,
   DragOverlay,
   DragStartEvent,
} from '@dnd-kit/core'
import { DnDStageAndTagPill } from './DnDStageAndTagPill'
import {
   ActiveStagesTab,
   AppointMentStagesTab,
   NurtureStagesTab,
   StoppedStagesTab,
} from './tabs'
import { useDnDStageMappingContext } from '../hooks/useDnDStageMappingContext'
import { TStageGroupIdentifier } from '../interfaces'
import {
   ArrowLeftMdIcon,
   ArrowRightMdIcon,
   SearchMagnifyingGlassIcon,
} from '@forwardflow/icons'

type TActiveTab = 'active-stages' | 'appointment' | 'nurture' | 'stopped'

const TAB_ORDER: TActiveTab[] = [
   'active-stages',
   'appointment',
   'nurture',
   'stopped',
]

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
   const [debouncedValue, setDebouncedValue] = useState<T>(value)

   useEffect(() => {
      const handler = setTimeout(() => {
         setDebouncedValue(value)
      }, delay)

      return () => {
         clearTimeout(handler)
      }
   }, [value, delay])

   return debouncedValue
}

export const DnDStageMappingTabs = () => {
   const { currentOrganization } = useAppStore()
   const {
      addStageToGroup,
      addTagToGroup,
      removeStageFromAllGroups,
      removeTagFromAllGroups,
      canAddItem,
      getStagesAndTagsByIdentifier,
      getStagesAndTagsByTimeline,
      stageMappingGroups,
      onPreviousStep,
      onNextStep,
      onSaveStageMappingGroups,
      showNavigation,
   } = useDnDStageMappingContext()
   const [activeTab, setActiveTab] = useState<TActiveTab>('active-stages')
   const [activeStage, setActiveStage] = useState<IStage | null>(null)
   const [stageSearch, setStageSearch] = useState('')
   const [tagSearch, setTagSearch] = useState('')

   const debouncedTagSearch = useDebounce(tagSearch, 300)

   const { data: fubStages, isLoading: isLoadingStages } = useGetFubStages(
      currentOrganization || '',
      10000,
      1,
      true
   )

   const { data: fubTags, isLoading: isLoadingTags } = useGetPeopleTags(
      currentOrganization || '',
      1,
      100,
      true,
      debouncedTagSearch
   )

   const stagesForSuggestion = useMemo(
      () =>
         fubStages?.data?.map((stage) => ({
            stageId: stage.stageId,
            stageName: stage.stageName,
         })) || [],
      [fubStages]
   )

   const tagsForSuggestion = useMemo(
      () =>
         fubTags?.data?.map((tag, index) => ({
            tagId: index + 1,
            tagName: tag.tagName,
         })) || [],
      [fubTags]
   )

   const { data: suggestedMappings, isLoading: isLoadingSuggestions } =
      useGetSuggestedStageMappings(stagesForSuggestion, tagsForSuggestion)

   const isSuggestionsLoading =
      isLoadingStages ||
      isLoadingTags ||
      isLoadingSuggestions ||
      (!suggestedMappings && stagesForSuggestion.length > 0 && tagsForSuggestion.length > 0)

   const stages = useMemo(
      () =>
         fubStages?.data?.map((stage) => ({
            id: stage.stageId.toString(),
            name: stage.stageName,
            description: '',
            type: 'stage' as 'stage' | 'tag',
         })) || [],
      [fubStages]
   )

   const tags = useMemo(
      () =>
         fubTags?.data?.map((tag) => ({
            id: tag.tagName,
            name: tag.tagName,
            description: '',
            type: 'tag' as 'stage' | 'tag',
         })) || [],
      [fubTags]
   )

   const [allStages, setAllStages] = useState<IStage[]>([])
   const [availableTags, setAvailableTags] = useState<IStage[]>([])
   const [suggestedStages, setSuggestedStages] = useState<
      (IStage & { targetGroup: string })[]
   >([])
   const [suggestedTags, setSuggestedTags] = useState<
      (IStage & { targetGroup: string })[]
   >([])

   // Map tabs to their corresponding group prefixes and type keys
   const TAB_TO_GROUPS: Record<TActiveTab, string[]> = {
      'active-stages': ['ff-closed-client', 'ff-under-contract', 'ff-active-client'],
      appointment: ['ff-appointment-set', 'ff-appointment-missed'],
      nurture: ['ff-nurturing'],
      stopped: ['ff-stopped', 'ff-other-stop', 'ff-has-agent', 'ff-do-not-contact'],
   }

   // Filter suggestions based on active tab
   const filteredSuggestedStages = useMemo(() => {
      const groups = TAB_TO_GROUPS[activeTab]
      if (groups.length === 0) return []
      return suggestedStages.filter((stage) => groups.includes(stage.targetGroup))
   }, [suggestedStages, activeTab])

   const filteredSuggestedTags = useMemo(() => {
      const groups = TAB_TO_GROUPS[activeTab]
      if (groups.length === 0) return []
      return suggestedTags.filter((tag) => groups.includes(tag.targetGroup))
   }, [suggestedTags, activeTab])

   // Filter stages based on search (frontend filtering)
   const availableStages = useMemo(() => {
      if (!stageSearch.trim()) {
         return allStages
      }
      const searchLower = stageSearch.toLowerCase()
      return allStages.filter((stage) =>
         stage.name.toLowerCase().includes(searchLower)
      )
   }, [allStages, stageSearch])

   const [showAllStages, setShowAllStages] = useState(false)
   const [showAllTags, setShowAllTags] = useState(false)

   const currentTabIndex = TAB_ORDER.indexOf(activeTab)
   const isFirstTab = currentTabIndex === 0
   const isLastTab = currentTabIndex === TAB_ORDER.length - 1

   const handlePrevTab = () => {
      if (!isFirstTab) {
         setActiveTab(TAB_ORDER[currentTabIndex - 1])
      } else {
         onPreviousStep?.()
      }
   }

   const handleNextTab = () => {
      onSaveStageMappingGroups?.(stageMappingGroups)
      if (!isLastTab) {
         setActiveTab(TAB_ORDER[currentTabIndex + 1])
      } else {
         onNextStep?.()
      }
   }

   // Update all stages when data changes (no search filtering here)
   useEffect(() => {
      if (stages.length >= 0) {
         // Filter out stages that are already assigned to groups
         const assignedStageIds = new Set(
            stageMappingGroups.flatMap((group) => {
               const topLevelIds = (group.stages || []).map((s) => s.id)
               const nestedIds =
                  group.types?.flatMap((type) =>
                     type.stages.map((s) => s.id)
                  ) || []
               return [...topLevelIds, ...nestedIds]
            })
         )
         const unassignedStages = stages.filter(
            (stage) => !assignedStageIds.has(stage.id)
         )
         setAllStages(unassignedStages)
      }
   }, [stages, stageMappingGroups])

   // Update available tags when search results change
   useEffect(() => {
      if (tags.length >= 0) {
         // Filter out tags that are already assigned to groups
         const assignedTagIds = new Set(
            stageMappingGroups.flatMap((group) => {
               const topLevelIds = (group.tags || []).map((t) => t.id)
               const nestedIds =
                  group.types?.flatMap((type) => type.tags.map((t) => t.id)) ||
                  []
               return [...topLevelIds, ...nestedIds]
            })
         )
         const unassignedTags = tags.filter(
            (tag) => !assignedTagIds.has(tag.id)
         )
         setAvailableTags(unassignedTags)
      }
   }, [tags, stageMappingGroups])

   useEffect(() => {
      if (suggestedMappings?.data) {
         // Extract all suggested stages from all groups with their target group
         const allSuggestedStages: (IStage & { targetGroup: string })[] = []
         const seenStageIds = new Set<string>()

         // Extract all suggested tags from all groups with their target group
         const allSuggestedTags: (IStage & { targetGroup: string })[] = []
         const seenTagIds = new Set<string>()

         suggestedMappings.data.forEach((mapping) => {
            const targetGroup = mapping.group

            // Add stages from direct mapping
            mapping.stages?.forEach((stage) => {
               const id = stage.id.toString()
               if (!seenStageIds.has(id)) {
                  seenStageIds.add(id)
                  allSuggestedStages.push({
                     id,
                     name: stage.name,
                     description: '',
                     type: 'stage',
                     targetGroup,
                  })
               }
            })

            // Add tags from direct mapping
            mapping.tags?.forEach((tag) => {
               const id = tag.name
               if (!seenTagIds.has(id)) {
                  seenTagIds.add(id)
                  allSuggestedTags.push({
                     id,
                     name: tag.name,
                     description: '',
                     type: 'tag',
                     targetGroup,
                  })
               }
            })

            // Add stages and tags from nested types (for ff-stopped)
            // Use the type.key as targetGroup for proper tab filtering
            mapping.types?.forEach((type) => {
               const typeKey = type.key

               type.stages?.forEach((stage) => {
                  const id = stage.id.toString()
                  if (!seenStageIds.has(id)) {
                     seenStageIds.add(id)
                     allSuggestedStages.push({
                        id,
                        name: stage.name,
                        description: '',
                        type: 'stage',
                        targetGroup: typeKey,
                     })
                  }
               })

               type.tags?.forEach((tag) => {
                  const id = tag.name
                  if (!seenTagIds.has(id)) {
                     seenTagIds.add(id)
                     allSuggestedTags.push({
                        id,
                        name: tag.name,
                        description: '',
                        type: 'tag',
                        targetGroup: typeKey,
                     })
                  }
               })
            })
         })

         // Filter out stages that are already assigned
         const assignedStageIds = new Set(
            stageMappingGroups.flatMap((group) => {
               const topLevelIds = (group.stages || []).map((s) => s.id)
               const nestedIds =
                  group.types?.flatMap((type) =>
                     type.stages.map((s) => s.id)
                  ) || []
               return [...topLevelIds, ...nestedIds]
            })
         )

         // Filter out tags that are already assigned
         const assignedTagIds = new Set(
            stageMappingGroups.flatMap((group) => {
               const topLevelIds = (group.tags || []).map((t) => t.id)
               const nestedIds =
                  group.types?.flatMap((type) => type.tags.map((t) => t.id)) ||
                  []
               return [...topLevelIds, ...nestedIds]
            })
         )

         const unassignedSuggestedStages = allSuggestedStages.filter(
            (stage) => !assignedStageIds.has(stage.id)
         )
         setSuggestedStages(unassignedSuggestedStages)

         const unassignedSuggestedTags = allSuggestedTags.filter(
            (tag) => !assignedTagIds.has(tag.id)
         )
         setSuggestedTags(unassignedSuggestedTags)
      }
   }, [suggestedMappings, stageMappingGroups])

   const handleDragStart = (event: DragStartEvent) => {
      const { active } = event
      const item =
         availableStages.find((s) => s.id === active.id) ||
         availableTags.find((t) => t.id === active.id) ||
         suggestedStages.find((s) => s.id === active.id) ||
         suggestedTags.find((t) => t.id === active.id)
      setActiveStage(item || null)
   }

   const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event

      setActiveStage(null)

      if (!over) return

      const itemId = active.id as string
      const targetDropId = over.id as string

      // Find the item in stages, tags, or suggested stages/tags
      const item =
         availableStages.find((s) => s.id === itemId) ||
         availableTags.find((t) => t.id === itemId) ||
         suggestedStages.find((s) => s.id === itemId) ||
         suggestedTags.find((t) => t.id === itemId)

      if (!item) return

      // Check if item came from suggestions
      const isFromSuggestedStages = suggestedStages.some((s) => s.id === itemId)
      const isFromSuggestedTags = suggestedTags.some((t) => t.id === itemId)

      const isTag = item.type === 'tag'

      // Check if the drop target is a nurturing group (format: "ff-nurturing::{timeline}")
      let targetGroupId: TStageGroupIdentifier
      let timeline: string | undefined

      if (targetDropId.startsWith('ff-nurturing::')) {
         targetGroupId = 'ff-nurturing'
         // Extract timeline from the drop ID (e.g., "ff-nurturing::0-3 Months" -> "0-3 Months")
         timeline = targetDropId.replace('ff-nurturing::', '')
      } else {
         targetGroupId = targetDropId as TStageGroupIdentifier
      }

      // Check if group allows multiple items
      const allowsMultiple = canAddItem(targetGroupId, isTag, timeline)

      if (!allowsMultiple) {
         // Replace existing items: remove all stages and tags from this group
         const { stages: existingStages, tags: existingTags } =
            targetGroupId === 'ff-nurturing' && timeline
               ? getStagesAndTagsByTimeline(timeline)
               : getStagesAndTagsByIdentifier(targetGroupId)

         // Remove all existing stages from the group and add back to available
         existingStages.forEach((stage) => {
            removeStageFromAllGroups(stage.id)
            // Add back to available if it's not the item being dropped
            if (stage.id !== itemId) {
               setAllStages((prev) => [...prev, stage])
            }
         })

         // Remove all existing tags from the group and add back to available
         existingTags.forEach((tag) => {
            removeTagFromAllGroups(tag.id)
            // Add back to available if it's not the item being dropped
            if (tag.id !== itemId) {
               setAvailableTags((prev) => [...prev, tag])
            }
         })
      }

      if (isTag) {
         // Remove tag from all groups first (in case it's being moved from another group)
         removeTagFromAllGroups(itemId)
         // Add tag to target group
         addTagToGroup(item, targetGroupId, timeline)
         // Remove tag from available tags
         setAvailableTags((prev) => prev.filter((t) => t.id !== itemId))
         // Also remove from suggested tags if it was there
         if (isFromSuggestedTags) {
            setSuggestedTags((prev) => prev.filter((t) => t.id !== itemId))
         }
      } else {
         // Remove stage from all groups first (in case it's being moved from another group)
         removeStageFromAllGroups(itemId)
         // Add stage to target group
         addStageToGroup(item, targetGroupId, timeline)
         // Remove stage from all stages list
         setAllStages((prev) => prev.filter((s) => s.id !== itemId))
         // Also remove from suggested stages if it was there
         if (isFromSuggestedStages) {
            setSuggestedStages((prev) => prev.filter((s) => s.id !== itemId))
         }
      }
   }

   const handleRemoveStage = (stageId: string) => {
      // Find the stage from the original stages data
      const stage = stages.find((s) => s.id === stageId)
      if (stage) {
         // Add back to available stages
         setAllStages((prev) => [...prev, stage])
      }
   }

   const handleRemoveTag = (tagId: string) => {
      // Find the tag from the original tags data
      const tag = tags.find((t) => t.id === tagId)
      if (tag) {
         // Add back to available tags
         setAvailableTags((prev) => [...prev, tag])
      }
   }

   return (
      <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
         <div className="grid grid-cols-[8fr_5fr] gap-5">
            <div className="rounded-2xl border border-white/10 bg-white/2 p-6">
               <div className="flex justify-between items-center">
                  <SegmentControl
                     value={activeTab}
                     onChange={(value) => setActiveTab(value as TActiveTab)}
                     data={[
                        { label: 'Active Stages', value: 'active-stages' },
                        { label: 'Appointment', value: 'appointment' },
                        { label: 'Nurture', value: 'nurture' },
                        { label: 'Stop', value: 'stopped' },
                     ]}
                  />
                  {showNavigation && (
                     <div className="flex gap-2">
                        <Button
                           variant="secondary"
                           onClick={handlePrevTab}
                           disabled={isFirstTab && !onPreviousStep}
                           className="px-3"
                        >
                           <ArrowLeftMdIcon width={15} height={15} /> Go back
                        </Button>
                        <Button
                           variant="primary"
                           onClick={handleNextTab}
                           disabled={isLastTab && !onNextStep}
                           className="px-3"
                        >
                           Next <ArrowRightMdIcon width={20} height={20} />
                        </Button>
                     </div>
                  )}
               </div>
               {(() => {
                  switch (activeTab) {
                     case 'active-stages':
                        return (
                           <ActiveStagesTab
                              onRemoveStage={handleRemoveStage}
                              onRemoveTag={handleRemoveTag}
                           />
                        )
                     case 'appointment':
                        return (
                           <AppointMentStagesTab
                              onRemoveStage={handleRemoveStage}
                              onRemoveTag={handleRemoveTag}
                           />
                        )
                     case 'stopped':
                        return (
                           <StoppedStagesTab
                              onRemoveStage={handleRemoveStage}
                              onRemoveTag={handleRemoveTag}
                           />
                        )
                     case 'nurture':
                        return (
                           <NurtureStagesTab
                              onRemoveStage={handleRemoveStage}
                              onRemoveTag={handleRemoveTag}
                           />
                        )
                     default:
                        break
                  }
               })()}
            </div>
            <div
               className="rounded-2xl border border-[#9F7BFF]/10"
               style={{
                  background:
                     'linear-gradient(180deg, #9F7BFF0A 0%, #EB69C20A 100%)',
               }}
            >
               <div className="px-6 py-4">
                  <div className="relative mb-5">
                     <SearchMagnifyingGlassIcon
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40"
                        width={16}
                        height={16}
                     />
                     <input
                        type="text"
                        value={stageSearch}
                        onChange={(e) => setStageSearch(e.target.value)}
                        placeholder="Search stages..."
                        className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors"
                     />
                  </div>
                  <div className="flex items-center justify-between mb-3">
                     <p className="text-white/90 text-sm">
                        Suggestions
                     </p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-3">
                     {isSuggestionsLoading ? (
                        // Skeleton loading state
                        <>
                           {[...Array(6)].map((_, i) => (
                              <div
                                 key={i}
                                 className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24"
                              />
                           ))}
                        </>
                     ) : filteredSuggestedStages.length > 0 ? (
                        filteredSuggestedStages.map((stage) => (
                           <DnDStageAndTagPill key={stage.id} stage={stage} isSuggestionPill={true} />
                        ))
                     ) : (
                        <p className="text-sm text-white/40">
                           No suggested stages available
                        </p>
                     )}
                  </div>
                  <div className="flex items-center justify-between mb-3">
                     <p className="text-white/90 text-sm">
                        All Stages ({availableStages.length})
                     </p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-3">
                     {isLoadingStages ? (
                        // Skeleton loading state
                        <>
                           {[...Array(6)].map((_, i) => (
                              <div
                                 key={i}
                                 className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24"
                              />
                           ))}
                        </>
                     ) : availableStages.length > 0 ? (
                        (showAllStages
                           ? availableStages
                           : availableStages.slice(0, 6)
                        ).map((stage) => (
                           <DnDStageAndTagPill key={stage.id} stage={stage} />
                        ))
                     ) : (
                        <p className="text-sm text-white/40">
                           {stageSearch
                              ? 'No stages found'
                              : 'All stages have been assigned'}
                        </p>
                     )}
                  </div>
                  {availableStages.length > 6 && (
                     <button
                        onClick={() => setShowAllStages(!showAllStages)}
                        className="w-full text-center text-white/60 hover:text-white/90 text-sm py-2 transition-colors flex items-center justify-center gap-2"
                     >
                        {showAllStages ? 'Show less Stages' : 'Show all Stages'}
                        <svg
                           className={`w-4 h-4 transition-transform ${
                              showAllStages ? 'rotate-180' : ''
                           }`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                           />
                        </svg>
                     </button>
                  )}
               </div>
               <div className="px-6 py-4 border-t border-white/10">
                  <div className="relative mb-5">
                     <SearchMagnifyingGlassIcon
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40"
                        width={16}
                        height={16}
                     />
                     <input
                        type="text"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        placeholder="Search tags..."
                        className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 focus:outline-none focus:border-white/30 transition-colors"
                     />
                  </div>
                  <div className="flex items-center justify-between mb-3">
                     <p className="text-white/90 text-sm">
                        Suggestions
                     </p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-3">
                     {isSuggestionsLoading ? (
                        // Skeleton loading state
                        <>
                           {[...Array(6)].map((_, i) => (
                              <div
                                 key={i}
                                 className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24"
                              />
                           ))}
                        </>
                     ) : filteredSuggestedTags.length > 0 ? (
                        filteredSuggestedTags.map((tag) => (
                           <DnDStageAndTagPill key={tag.id} stage={tag} isSuggestionPill={true} />
                        ))
                     ) : (
                        <p className="text-sm text-white/40">
                           No suggested tags available
                        </p>
                     )}
                  </div>
                  <div className="flex items-center justify-between mb-3">
                     <p className="text-white/90 text-sm">
                        All Tags ({availableTags.length})
                     </p>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mb-3">
                     {isLoadingTags ? (
                        // Skeleton loading state
                        <>
                           {[...Array(6)].map((_, i) => (
                              <div
                                 key={i}
                                 className="rounded-[8px] bg-white/10 animate-pulse h-8 w-24"
                              />
                           ))}
                        </>
                     ) : availableTags.length > 0 ? (
                        (showAllTags
                           ? availableTags
                           : availableTags.slice(0, 10)
                        ).map((tag) => (
                           <DnDStageAndTagPill key={tag.name} stage={tag} />
                        ))
                     ) : (
                        <p className="text-sm text-white/40">
                           {tagSearch
                              ? 'No tags found'
                              : 'All tags have been assigned'}
                        </p>
                     )}
                  </div>
                  {availableTags.length > 6 && (
                     <button
                        onClick={() => setShowAllTags(!showAllTags)}
                        className="w-full text-center text-white/60 hover:text-white/90 text-sm py-2 transition-colors flex items-center justify-center gap-2"
                     >
                        {showAllTags ? 'Show less Tags' : 'Show all Tags'}
                        <svg
                           className={`w-4 h-4 transition-transform ${
                              showAllTags ? 'rotate-180' : ''
                           }`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                        >
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                           />
                        </svg>
                     </button>
                  )}
               </div>
            </div>
         </div>
         <DragOverlay>
            {activeStage ? (
               <div className="rounded-[8px] bg-white/20 shadow-[0_4px_4px_0_rgba(0,0,0,0.25)] py-1.5 px-8 text-sm w-fit opacity-50">
                  <p>{activeStage.name}</p>
               </div>
            ) : null}
         </DragOverlay>
      </DndContext>
   )
}
